import { useQuery } from '@tanstack/react-query';
import { Banknote, Building2, CalendarClock, CalendarDays, HandCoins, IdCard, Mail, Phone, ShieldCheck, WalletCards } from 'lucide-react';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { useAuth, type Role } from '../lib/AuthContext';
import { useBranches, useLeaveTypes } from '../lib/options';
import { Loader } from '../components/ui/Spinner';

type RoleAssignment = { id: string; role: Role; branchId: string | null; regionId: string | null };
type BranchAssignment = { id: string; branchId: string; assignedFrom: string; assignedTo: string | null; isRoaming: boolean };
type StaffDetail = {
  id: string; name: string; phone: string; email: string | null; employeeCode: string; joiningDate: string; isActive: boolean; roles: RoleAssignment[]; branchHistory: BranchAssignment[];
  designation: string | null; department: string | null;
  panNumber?: string | null; aadhaarNumber?: string | null; esicNumber?: string | null; uanNumber?: string | null; bankAccountHolderName?: string | null; bankAccountNumber?: string | null; bankIfsc?: string | null; bankName?: string | null;
};
type LeaveBalance = { leaveTypeId: string; year: number; entitled: string; used: string; carriedForward: string };

const ROLE_LABELS: Record<Role, string> = { staff: 'Staff', branch_head: 'Branch Head', region_head: 'Region Head', general_head: 'General Head', hr: 'HR', admin: 'Admin' };

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

/** "My Profile" — a personal, ID-card-style read-only view of your own record. Every role
 * sees this; nobody (including HR/Admin) can edit their own profile from here, matching the
 * self-edit rule enforced everywhere else in the app. */
export function ProfilePage() {
  const { user } = useAuth();
  const id = user?.id ?? user?.sub;
  const branches = useBranches();
  const leaveTypes = useLeaveTypes();
  const year = new Date().getFullYear();

  const staffQuery = useQuery({ queryKey: ['staff', id], queryFn: async () => (await api.get<StaffDetail>(`/api/staff/${id}`)).data, enabled: !!id });
  const balances = useQuery({ queryKey: ['leave-balances', id, year], queryFn: async () => (await api.get<LeaveBalance[]>('/api/leave-balances', { params: { year } })).data, enabled: !!id });

  if (staffQuery.isLoading) return <Loader />;
  if (!staffQuery.data) return <div className="card text-center text-muted">Profile not found.</div>;
  const staff = staffQuery.data;

  const currentBranchId = staff.branchHistory.find((a) => a.assignedTo === null)?.branchId;
  const currentBranch = branches.data?.find((b) => b.id === currentBranchId);
  const hasKyc = staff.panNumber !== undefined;
  const byType = new Map((balances.data ?? []).map((b) => [b.leaveTypeId, b]));

  return (
    <>
      <div className="mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-brand-600 to-brand-700 text-white shadow-card">
        <div className="flex items-center gap-4 p-6">
          <div className="flex size-16 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-2xl font-bold backdrop-blur sm:size-20 sm:text-3xl">{initials(staff.name)}</div>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-bold sm:text-3xl">{staff.name}</h1>
            {(staff.designation || staff.department) && <p className="mt-1 text-sm text-brand-100">{[staff.designation, staff.department].filter(Boolean).join(' • ')}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {staff.roles.map((r) => <span key={r.id} className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold"><ShieldCheck size={12} /> {ROLE_LABELS[r.role]}</span>)}
              {!staff.isActive && <span className="rounded-full bg-red-500 px-3 py-1 text-xs font-bold">Inactive</span>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-px bg-white/10 sm:grid-cols-4">
          <div className="bg-brand-700/40 p-4">
            <p className="flex items-center gap-1.5 text-xs text-brand-100"><IdCard size={13} /> Employee code</p>
            <p className="mt-1 truncate font-mono text-sm font-semibold tracking-wide">{staff.employeeCode}</p>
          </div>
          <div className="bg-brand-700/40 p-4">
            <p className="flex items-center gap-1.5 text-xs text-brand-100"><Phone size={13} /> Phone</p>
            <p className="mt-1 truncate text-sm font-semibold">{staff.phone}</p>
          </div>
          <div className="bg-brand-700/40 p-4">
            <p className="flex items-center gap-1.5 text-xs text-brand-100"><Building2 size={13} /> Branch</p>
            <p className="mt-1 truncate text-sm font-semibold">{currentBranch?.name ?? '—'}</p>
          </div>
          <div className="bg-brand-700/40 p-4">
            <p className="flex items-center gap-1.5 text-xs text-brand-100"><CalendarDays size={13} /> Joined</p>
            <p className="mt-1 truncate text-sm font-semibold">{new Date(`${staff.joiningDate}T00:00:00`).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {staff.email && (
          <div className="card flex items-center gap-3 lg:col-span-2">
            <Mail size={18} className="shrink-0 text-muted" />
            <span className="text-sm">{staff.email}</span>
          </div>
        )}

        {hasKyc && (
          <div className="card">
            <h3 className="flex items-center gap-2 font-bold"><IdCard size={18} /> KYC details</h3>
            <p className="mt-3 text-sm text-muted">PAN</p>
            <p className="font-mono text-sm font-semibold">{staff.panNumber ?? 'Not recorded'}</p>
            <p className="mt-3 text-sm text-muted">Aadhaar</p>
            <p className="font-mono text-sm font-semibold">{staff.aadhaarNumber ?? 'Not recorded'}</p>
            <p className="mt-3 text-sm text-muted">ESIC number</p>
            <p className="font-mono text-sm font-semibold">{staff.esicNumber ?? 'Not recorded'}</p>
            <p className="mt-3 text-sm text-muted">UAN number</p>
            <p className="font-mono text-sm font-semibold">{staff.uanNumber ?? 'Not recorded'}</p>
          </div>
        )}

        {hasKyc && (
          <div className="card">
            <h3 className="flex items-center gap-2 font-bold"><Banknote size={18} /> Bank account</h3>
            <p className="mt-3 text-sm text-muted">Account holder</p>
            <p className="text-sm font-semibold">{staff.bankAccountHolderName ?? 'Not recorded'}{staff.bankName ? ` • ${staff.bankName}` : ''}</p>
            <p className="mt-3 text-sm text-muted">Account number</p>
            <p className="font-mono text-sm font-semibold">{staff.bankAccountNumber ? `${staff.bankAccountNumber}${staff.bankIfsc ? ` • ${staff.bankIfsc}` : ''}` : 'Not recorded'}</p>
          </div>
        )}

        <div className="card lg:col-span-2">
          <h3 className="flex items-center gap-2 font-bold"><CalendarClock size={18} /> Leave balances ({year})</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {leaveTypes.data?.filter((t) => t.isActive).map((type) => {
              const b = byType.get(type.id);
              const remaining = b ? (Number(b.entitled) + Number(b.carriedForward) - Number(b.used)).toFixed(1) : '—';
              return (
                <div key={type.id} className="rounded-xl bg-canvas p-3">
                  <p className="text-sm text-muted">{type.name}</p>
                  <p className="mt-1 text-2xl font-bold text-brand-700">{remaining}</p>
                  <p className="mt-0.5 text-xs text-muted">of {b?.entitled ?? type.yearlyEntitlement} days</p>
                </div>
              );
            })}
            {leaveTypes.data?.filter((t) => t.isActive).length === 0 && <p className="text-sm text-muted">No leave types configured yet.</p>}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Link href="/leave" className="btn-secondary"><CalendarClock size={16} /> Apply for leave</Link>
        <Link href={`/loans?userId=${staff.id}`} className="btn-secondary"><HandCoins size={16} /> My loans &amp; advances</Link>
        <Link href="/my-payslips" className="btn-secondary"><WalletCards size={16} /> My payslips</Link>
      </div>
    </>
  );
}
